import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { PresenterDepth } from "../domain";
import {
  DEPTH_STORAGE_KEY,
  depthDefinitions,
  depthFromSearch,
  isPresenterDepth,
  resolveInitialDepth,
  searchWithDepth,
  searchWithoutInvalidDepth,
  type DepthContent,
  type DepthDefinition,
} from "./depth";

type DepthContextValue = {
  announcement: string;
  definition: DepthDefinition;
  depth: PresenterDepth;
  setDepth: (depth: PresenterDepth) => void;
};

const DepthContext = createContext<DepthContextValue | null>(null);

function readStoredDepth(): string | null {
  try {
    return window.localStorage.getItem(DEPTH_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredDepth(depth: PresenterDepth) {
  try {
    window.localStorage.setItem(DEPTH_STORAGE_KEY, depth);
  } catch {
    // The preference still works for the current session when storage is unavailable.
  }
}

function replaceDepthUrl(depth: PresenterDepth) {
  const nextSearch = searchWithDepth(window.location.search, depth);
  const nextUrl = `${window.location.pathname}${nextSearch}${window.location.hash}`;
  window.history.replaceState(window.history.state, "", nextUrl);
}

function cleanInvalidDepthUrl() {
  if (depthFromSearch(window.location.search) !== "invalid") return;
  const nextSearch = searchWithoutInvalidDepth(window.location.search);
  const nextUrl = `${window.location.pathname}${nextSearch}${window.location.hash}`;
  window.history.replaceState(window.history.state, "", nextUrl);
}

export function DepthProvider({ children }: { children: ReactNode }) {
  const [depth, updateDepth] = useState<PresenterDepth>(() =>
    resolveInitialDepth(window.location.search, readStoredDepth()),
  );
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    document.documentElement.dataset.presentationDepth = depth;
    return () => {
      delete document.documentElement.dataset.presentationDepth;
    };
  }, [depth]);

  useEffect(() => {
    const urlDepth = depthFromSearch(window.location.search);
    const storedDepth = readStoredDepth();
    if (urlDepth === "invalid") {
      writeStoredDepth("operations");
      cleanInvalidDepthUrl();
    } else if (urlDepth) {
      writeStoredDepth(urlDepth);
    } else if (isPresenterDepth(storedDepth)) {
      replaceDepthUrl(storedDepth);
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const urlDepth = depthFromSearch(window.location.search);
      const nextDepth = urlDepth === "invalid"
        ? "operations"
        : urlDepth ?? (isPresenterDepth(readStoredDepth()) ? readStoredDepth() as PresenterDepth : "operations");
      updateDepth(nextDepth);
      if (urlDepth === "invalid") {
        writeStoredDepth("operations");
        cleanInvalidDepthUrl();
      } else if (urlDepth) {
        writeStoredDepth(urlDepth);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const setDepth = useCallback((nextDepth: PresenterDepth) => {
    updateDepth(nextDepth);
    writeStoredDepth(nextDepth);
    replaceDepthUrl(nextDepth);
    setAnnouncement(`Explanation depth changed to ${depthDefinitions[nextDepth].label}.`);
  }, []);

  const value = useMemo<DepthContextValue>(
    () => ({
      announcement,
      definition: depthDefinitions[depth],
      depth,
      setDepth,
    }),
    [announcement, depth, setDepth],
  );

  return <DepthContext.Provider value={value}>{children}</DepthContext.Provider>;
}

export function useDepth() {
  const context = useContext(DepthContext);
  if (!context) throw new Error("useDepth must be used inside DepthProvider");
  return context;
}

export function useDepthValue<T>(content: DepthContent<T>): T {
  const { depth } = useDepth();
  return content[depth];
}
